/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { ReactDatePicker, usePrintDispatch } from '@mummssoftware/common-ui';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { Button } from '@material-ui/core';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import SvgIcon from '@material-ui/core/SvgIcon';
import Assignment from '@material-ui/icons/Assignment';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import {
  MultiPerPage,
  OnePerPage,
  Orders,
  ProgressNotes,
  emptyText,
} from '@mummssoftware/utils/constants';
import { Printing, ConfigService, isResponse } from '@mummssoftware/utils';
import { PrintProgress } from '@mummssoftware/utils/api/Printing';
import { PRINT_PRECONFIG_IDS } from '@mummssoftware/utils/api/ConfigService';
import { dateToMoment } from '@mummssoftware/utils/dates';

import { Context } from '../../context';
import { DashboardContext } from '../Context/contexts/DashboardContext';
import {
  SET_PRINT_CONFIG,
  SET_PRINT_INTERFACE_OPEN,
  RESET_WIDGETS_READY,
} from '../Context/constants';
import defaults from '../../translations/en.json';
import {
  PrintInterfaceDiv,
  CalendarDiv,
  SubmitDiv,
  PrintSelectionDiv,
  PrintOptionsDiv,
  OptionsBox,
} from '../../components';

import {
  printOptionsConfig,
  RadioPrintConfig,
  CheckOptionPrintConfig,
  SingleDay,
} from './constants';
import { PrintInterfaceContext } from './PrintInterfaceDialog';
import {
  PRINT_PRECONFIG_NAMES,
  resolvePrintPreconfig,
} from './printPreconfigBuilders';
import {
  mergePreconfigWidgets,
  radioOptionsWithoutIdg,
  widgetHasIdgFilter,
  widgetsSnapshotKey,
} from './printPreconfigMerge';

type PrintInterfaceProps = {
  config: mumms.WidgetConfig[];
  printConfigNames: Record<mumms.ComponentName, string>;
  /** Dialog open state; closing the dialog clears the pending preconfig-save answer. */
  dialogOpen?: boolean;
};
/**
 * Floor for a radio option slot: short labels pad out to this so options line up
 * column-wise across widget rows, while longer labels are free to grow past it.
 */
const RADIO_OPTION_MIN_WIDTH = '4.15rem';

/**
 * Widget name column. Sized to the longest configured widgetTitle
 * ('Care Plan - Visit Compliance') so the radio options start as far left as
 * possible without the names wrapping to a second line.
 */
const WIDGET_LABEL_WIDTH = '14.5rem';

const useStyles = makeStyles((theme) => ({
  /**
   * Tighter print dialog: scope label typography so theme Typography.body1 does not win.
   * `&&` duplicates the class for specificity without !important.
   */
  printRoot: {
    '&& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      lineHeight: 1.35,
    },
    '& .react-datepicker__input-container input': {
      fontSize: '0.8125rem',
      lineHeight: 1.35,
    },
  },
  dateRange: {
    display: 'flex',
    justifyContent: 'center',
    alignSelf: 'center',
    // NOTE there is a maxWidth as by default more space than needed for mumms standards
    maxWidth: '340px',
    margin: 5,
  },
  formControl: {
    marginTop: theme.spacing(0.25),
    marginBottom: theme.spacing(0.25),
    alignItems: 'center',
  },
  widgetFormControl: {
    marginTop: 0,
    marginBottom: 0,
    alignItems: 'center',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignContent: 'center',
    // NOTE there is a maxWidth as by default more space than needed for mumms standards
    maxWidth: '300px',
    flexWrap: 'wrap',
  },
  progressNotesRadioWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  /**
   * ProgressNotes renders two levels of options, so both wrap inside the column
   * rather than overflowing it, and a rule separates the second level.
   */
  progressNotesPrimaryRow: {
    flexWrap: 'wrap',
  },
  progressNotesSecondaryRow: {
    flexWrap: 'wrap',
    alignSelf: 'stretch',
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(0.5),
    paddingTop: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
  printRadioGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignContent: 'center',
    maxWidth: '480px',
    flexWrap: 'wrap',
  },
  radioLabel: {
    margin: 0,
    marginRight: theme.spacing(0.55),
    whiteSpace: 'nowrap',
    overflow: 'visible',
  },
  /** Options share a minimum slot but grow to fit their label. */
  radioOption: {
    minWidth: RADIO_OPTION_MIN_WIDTH,
    flex: '0 1 auto',
  },
  checkBox: {
    color: `${theme.palette.primary.main}`,
    padding: theme.spacing(0.5),
  },
  /** Shared tighter hit area for small Radio / secondary Checkbox (below MUI default padding). */
  denseToggle: {
    padding: theme.spacing(0.7),
  },
  // NOTE this is for Meds radio buttons to align w Manifest
  formControlLabel: {
    '&.MuiFormControlLabel-root': {
      marginBottom: 0,
      marginTop: 0,
      minHeight: 0,
      minWidth: '135px',
    },
  },
  preconfigRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  preconfigRowInner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  preconfigLabel: {
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.875rem',
    lineHeight: 1.35,
    margin: 0,
  },
  preconfigSelect: {
    minWidth: 100,
    // Slightly shorter outlined select for Preconfigs
    '& .MuiSelect-select': {
      paddingTop: theme.spacing(0.75),
      paddingBottom: theme.spacing(0.75),
    },
  },
  /** Smaller than row labels (0.875rem); closed Select field + list (see menu paper). */
  preconfigSelectValue: {
    fontSize: '0.8125rem',
    lineHeight: 1.35,
  },
  preconfigSelectMenuPaper: {
    '& .MuiMenuItem-root': {
      fontSize: '0.8125rem',
      lineHeight: 1.35,
      minHeight: 28,
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0.25),
    },
  },
  /** Notes / Filter menus: match row radio label typography (0.875rem). */
  optionSelectMenuPaper: {
    '& .MuiMenuItem-root': {
      fontSize: '0.875rem',
      lineHeight: 1.35,
      minHeight: 28,
      paddingTop: theme.spacing(0.25),
      paddingBottom: theme.spacing(0.25),
    },
  },
  optionIconButton: {
    padding: theme.spacing(0.5),
  },
  optionIcon: {
    // The clipboard body sits below a clip tab, so it needs more than the
    // checkbox's 1.25rem to read at the same size as the IDG checkbox.
    fontSize: '1.5rem',
    display: 'block',
  },
  /**
   * Notes icon: "none" layers an accent outline over a grey clipboard. Selected
   * modes (all / date range) are a single primary fill — no outline overlay.
   */
  notesIconStack: {
    position: 'relative',
    display: 'inline-flex',
  },
  /** Keeps the frame / slash / clock layer registered over the filled glyph. */
  notesIconOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  notesIconOutline: {
    color: theme.palette.text.secondary,
  },
  /**
   * The clock knocks its hands out of a solid disc, so it needs the clipboard
   * cut away behind it — otherwise the body shows through and the disc reads
   * solid. Material's own PendingActions cuts the clipboard the same way.
   */
  notesIconClockCutout: {
    fill: theme.palette.background.paper,
  },
  notesIconFillActive: {
    // Matches the checked IDG checkbox (Checkbox color="primary").
    color: theme.palette.primary.main,
  },
  notesIconFillIdle: {
    color: theme.palette.grey[400],
  },
  widgetLabel: {
    width: WIDGET_LABEL_WIDTH,
    marginRight: 0,
  },
  datePickerPopper: {
    zIndex: theme.zIndex.modal + 1,
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  /**
   * IDG / Notes columns. The header row and every widget row share these classes
   * inside the same FormGroup so the labels sit directly over their controls.
   */
  optionHeaderRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  optionColumns: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  optionColumn: {
    width: 44,
    flex: '0 0 44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionHeader: {
    fontSize: '0.875rem',
    lineHeight: 1.35,
    margin: 0,
    whiteSpace: 'nowrap',
  },
}));
const translations: NonNullable<mumms.JSONObject> = defaults;

/**
 * Material `AssignmentOutlined` with its three content-line rects removed, so it
 * draws only the clipboard frame and can be layered over a filled `Assignment`.
 */
const ASSIGNMENT_FRAME_PATH =
  'M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-.14 0-.27.01-.4.04-.39.08-.74.28-1.01.55-.18.18-.33.4-.43.64-.1.23-.16.49-.16.77v14c0 .27.06.54.16.78s.25.45.43.64c.27.27.62.47 1.01.55.13.02.26.03.4.03h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7-.25c.41 0 .75.34.75.75s-.34.75-.75.75-.75-.34-.75-.75.34-.75.75-.75zM19 19H5V5h14v14z';

/** Diagonal bar of Material `ContentPasteOff`, marking notes as excluded. */
const NOTES_SLASH_PATH = 'M2.1 3.51 3.51 2.1 21.9 20.49 20.49 21.9z';

/** Clock of Material `PendingActions`, marking the date-range window. */
const NOTES_CLOCK_PATH =
  'M17 12c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5m1.65 7.35L16.5 17.2V14h1v2.79l1.85 1.85z';

const NOTES_MODE_MARK: Partial<Record<mumms.notesMode, string>> = {
  none: NOTES_SLASH_PATH,
  dateRange: NOTES_CLOCK_PATH,
};

/**
 * "None" matches the unchecked IDG checkbox: grey body, accent outline, slash.
 * All notes / date range are one primary fill, with a clock on date range.
 */
const NotesIcon = ({ mode }: { mode: mumms.notesMode }) => {
  const classes = useStyles();
  const selected = mode !== 'none';
  const mark = NOTES_MODE_MARK[mode];

  return (
    <span className={classes.notesIconStack}>
      <Assignment
        className={`${classes.optionIcon} ${
          selected ? classes.notesIconFillActive : classes.notesIconFillIdle
        }`}
        titleAccess="Notes options"
      />
      {(mark || !selected) && (
        <SvgIcon
          className={`${classes.optionIcon} ${classes.notesIconOverlay} ${
            selected ? classes.notesIconFillActive : classes.notesIconOutline
          }`}
        >
          {!selected && <path d={ASSIGNMENT_FRAME_PATH} />}
          {mode === 'dateRange' && (
            <circle
              cx="17"
              cy="17"
              r="6.1"
              className={classes.notesIconClockCutout}
            />
          )}
          {mark && <path d={mark} />}
        </SvgIcon>
      )}
    </span>
  );
};

type OptionIconMenuProps = {
  icon: React.ReactElement;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
};

/**
 * Notes / Filter option menu: an icon button that opens the options as a dropdown.
 * Keeps the print rows compact — the current choice shows as the checked item.
 */
const OptionIconMenu = ({
  icon,
  value,
  onChange,
  options,
}: OptionIconMenuProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <OptionsBox className={classes.optionColumn}>
      <IconButton
        size="small"
        className={classes.optionIconButton}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {icon}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ className: classes.optionSelectMenuPaper }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              setAnchorEl(null);
              onChange(option.value);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </OptionsBox>
  );
};

type PrintAction = 'print' | 'fax' | 'email';

export const PrintInterface = ({
  config,
  printConfigNames,
  dialogOpen,
}: PrintInterfaceProps) => {
  const classes = useStyles();
  const { dispatch } = useContext(DashboardContext);
  const {
    patient,
    keycloak,
    agency,
    currentUser,
    siteId,
    isMummsAdmin,
    isImpersonating,
  } = useContext(Context);
  const canManagePreconfigs =
    !!isMummsAdmin || !!isImpersonating || !!currentUser?.isHospiceAdmin;
  const { setLoading, setProgress } = useContext(PrintInterfaceContext);
  const printConfigRef = useRef<any>(null);

  const printingService = useMemo(
    () =>
      new Printing({
        url: window.env.hosts.printingService as string,
        keycloak,
        agency,
        currentUser,
      }),
    [],
  );

  const configService = useMemo(() => {
    const url = (window.env.hosts as any).configService as string | undefined;
    if (!url || !keycloak || !agency) return null;
    return new ConfigService({ url, keycloak, agency });
  }, [keycloak, agency]);

  const [serverPreconfigs, setServerPreconfigs] = useState<
    mumms.PrintPreconfig[] | null
  >(null);

  useEffect(() => {
    if (!configService) {
      setServerPreconfigs(null);
      return;
    }
    configService.listPrintPreconfigs().then((res) => {
      if (!res || isResponse(res)) {
        setServerPreconfigs(null);
        return;
      }
      setServerPreconfigs(res as mumms.PrintPreconfig[]);
    });
  }, [configService]);

  // upon componentDidMount is the time to reset widgets ready and printconfig
  useEffect(() => {
    dispatch({
      type: RESET_WIDGETS_READY,
    });
    // reset it for the keys of the dashboard print components be reset
    dispatch({
      type: SET_PRINT_CONFIG,
      payload: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dashboardPrintConfig: mumms.ComponentName[] = [...config].map(
    (widget: mumms.WidgetConfig) => widget.componentName,
  );

  const radioPrintConfig = [...config].reduce(
    (seed, widget: mumms.WidgetConfig) => {
      if (
        widget.printable &&
        widget.printConfig &&
        widget.printConfig.options
      ) {
        const { componentName, printConfig } = widget;
        seed[componentName] = radioOptionsWithoutIdg(
          printConfig.options as Record<string, string>,
        );
      }
      return seed;
    },
    {} as RadioPrintConfig,
  );

  const radioPrintConfigRaw = [...config].reduce(
    (seed, widget: mumms.WidgetConfig) => {
      if (
        widget.printable &&
        widget.printConfig &&
        widget.printConfig.options
      ) {
        seed[widget.componentName] = widget.printConfig.options as Record<
          string,
          string
        >;
      }
      return seed;
    },
    {} as Record<string, Record<string, string>>,
  );

  /** Prefer env `defaultOption`; else first string value in `printConfig.options` (insertion order). */
  const defaultRadioViewForWidget = (name: mumms.ComponentName) => {
    const opts = radioPrintConfig[name];
    if (!opts) return undefined;
    const explicit = opts.defaultOption;
    if (explicit != null && explicit !== '') return explicit;
    return Object.values(opts).find((v) => v !== '') as string | undefined;
  };

  const checkOptionPrintConfig: CheckOptionPrintConfig = [...config].reduce(
    (seed, widget: mumms.WidgetConfig) => {
      if (
        widget.printable &&
        widget.printConfig &&
        widget.printConfig.checkboxes
      ) {
        const { componentName, printConfig } = widget;
        seed[componentName] = printConfig.checkboxes;
      }
      return seed;
    },
    {} as any,
  );

  const printState = dashboardPrintConfig.reduce((seed, widgetName) => {
    seed[widgetName] = false;
    return seed;
  }, {} as mumms.PrintWidgetConfigInterface);

  // filter the widgets with radio button
  const radioStateConfig: mumms.ComponentName[] = [...config]
    .filter(
      (widget: mumms.WidgetConfig) =>
        widget.printConfig && widget.printConfig.options,
    )
    .map((widget: mumms.WidgetConfig) => widget.componentName);
  const radioChoicePrintState = [...radioStateConfig].reduce(
    (seed, widgetName) => {
      seed[widgetName] = '';
      return seed;
    },
    {} as mumms.ChoicePrintStateInterface,
  );

  const [state, setState] = useState(printState);
  const stateEntries = Object.entries(state);

  const noWidgetsSelected = dashboardPrintConfig.every(
    (componentName) => !state[componentName],
  );

  const handleChange = (event: any) => {
    clearPreconfigSelection();
    const { checked } = event.target;
    // eslint-disable-next-line prefer-destructuring
    const name: mumms.ComponentName = event.target.name;
    let widgetChecked = checked;
    if (checked) {
      const notesDefault = checkOptionPrintConfig[name]
        ? { notesMode: 'none' as mumms.notesMode }
        : {};
      widgetChecked =
        (radioPrintConfig[name as mumms.ComponentName] && {
          view: radioState[name] || defaultRadioViewForWidget(name),
          ...notesDefault,
        }) ||
        (checkOptionPrintConfig[name] ? notesDefault : checked);
    }
    setState({
      ...state,
      [name]: widgetChecked,
    });
  };

  // needed to set a default from / to date range for manifest date picker.
  const [fromDate, setFromDate] = useState<Date | null | undefined>(null);
  const [toDate, setToDate] = useState<Date | null | undefined>(null);

  const [dateError, setDateError] = useState<undefined | boolean>(undefined);

  const [preconfigSelectValue, setPreconfigSelectValue] = useState<string>('');
  const [lastAppliedPreconfigId, setLastAppliedPreconfigId] = useState<
    mumms.PrintPreconfigId | ''
  >('');
  const [lastAppliedWidgets, setLastAppliedWidgets] = useState<
    mumms.PrintPreconfig['widgets'] | null
  >(null);
  // Action awaiting the Yes/No answer to the "save preconfig changes" prompt.
  const [pendingAction, setPendingAction] = useState<PrintAction | null>(null);
  // Action whose preconfig save failed; offered again without saving.
  const [saveFailedAction, setSaveFailedAction] = useState<PrintAction | null>(
    null,
  );

  // RADIO PRINT options values
  const [printOptionsState, setPrintOptionsState] = React.useState(
    printOptionsConfig.defaultOption,
  );

  const isPrintDateRange =
    printOptionsState !== printOptionsConfig.defaultOption;

  useEffect(() => {
    if (!isPrintDateRange) {
      setFromDate(undefined);
      setToDate(undefined);
    } else {
      // Only seed defaults when turning on date range with no dates yet.
      // Do not overwrite preconfig (e.g. IDG: 14 days ago → today) or user-picked values.
      setFromDate((prev) => (prev != null ? prev : new Date()));
      setToDate((prev) => (prev != null ? prev : new Date()));
    }
  }, [isPrintDateRange]);

  const handleFromDateChange = (date: any) => {
    clearPreconfigSelection();
    setFromDate(date);
    if (printOptionsState === SingleDay) setToDate(date);
  };

  const handleToDateChange = (date: any) => {
    clearPreconfigSelection();
    setToDate(date);
  };

  useEffect(() => {
    if (isPrintDateRange && fromDate && toDate) {
      setDateError(undefined);
    }
  }, [fromDate, toDate]);

  const submittedState = {
    ...state,
    fromDate: isPrintDateRange ? [fromDate] : undefined,
    toDate: isPrintDateRange ? [toDate] : undefined,
  };

  const widgetsToPrint = submittedState && {
    ...submittedState,
  };

  widgetsToPrint && delete widgetsToPrint?.fromDate;
  widgetsToPrint && delete widgetsToPrint?.toDate;

  const onProgressChange = (progress: PrintProgress) => {
    setProgress(progress);
  };

  const closePrintInterface = () =>
    dispatch({
      type: SET_PRINT_INTERFACE_OPEN,
      payload: false,
    });

  const getPrintPromise = useCallback(
    (faxInstructions: any, emailInstructions: any) =>
      printingService.printDaChart(
        printConfigRef.current,
        patient!,
        'Print The Chart',
        onProgressChange,
        faxInstructions,
        undefined,
        emailInstructions,
      ),
    [printingService, patient],
  );

  const { print, fax, email } = usePrintDispatch(
    getPrintPromise,
    () => setLoading(true),
    () => {
      setLoading(false);
      setProgress(null);
    },
  );

  const runDispatch = (action: () => void) => {
    if (!(patient && agency && keycloak)) return;
    const dateerror = isPrintDateRange && (!fromDate || !toDate);
    setDateError(dateerror);
    if (dateerror) return;

    const fromDateFormatted =
      fromDate && dateToMoment(fromDate).format('YYYY-MM-DD');
    const toDateFormatted = toDate && dateToMoment(toDate).format('YYYY-MM-DD');

    printConfigRef.current = submittedState && {
      ...submittedState,
      fromDate: fromDateFormatted,
      toDate: toDateFormatted,
    };

    closePrintInterface();
    action();
  };

  const executePrintOrFax = (wantFax: boolean) => {
    runDispatch(wantFax ? fax : print);
  };

  const preconfigIsDirty =
    !!lastAppliedPreconfigId &&
    !!lastAppliedWidgets &&
    widgetsSnapshotKey(
      mergePreconfigWidgets(lastAppliedWidgets, dashboardPrintConfig, state),
    ) !== widgetsSnapshotKey(lastAppliedWidgets);

  const performAction = (action: PrintAction) => {
    if (action === 'email') {
      runDispatch(email);
      return;
    }
    executePrintOrFax(action === 'fax');
  };

  // Admins who changed a preconfig must answer Yes/No before the action runs.
  const requestAction = (action: PrintAction) => {
    if (canManagePreconfigs && preconfigIsDirty && configService) {
      setPendingAction(action);
      return;
    }
    performAction(action);
  };

  const onSubmit = () => requestAction('print');
  const onFax = () => requestAction('fax');
  const onEmail = () => requestAction('email');

  const savePreconfigThenPerform = async (action: PrintAction) => {
    if (!configService || !lastAppliedPreconfigId || !lastAppliedWidgets) {
      performAction(action);
      return;
    }
    const merged = mergePreconfigWidgets(
      lastAppliedWidgets,
      dashboardPrintConfig,
      state,
    );
    const res = await configService.putPrintPreconfig(
      lastAppliedPreconfigId,
      merged,
    );
    if (!res || isResponse(res)) {
      // eslint-disable-next-line no-console
      console.error(
        `[print preconfig] save failed for ${lastAppliedPreconfigId}`,
        isResponse(res) ? `${res.status} ${res.statusText}` : res,
      );
      setSaveFailedAction(action);
      return;
    }
    const saved = res as mumms.PrintPreconfig;
    setLastAppliedWidgets(saved.widgets);
    const refreshed = await configService.listPrintPreconfigs();
    if (refreshed && !isResponse(refreshed)) {
      setServerPreconfigs(refreshed as mumms.PrintPreconfig[]);
    }
    performAction(action);
  };

  const answerSavePrompt = (save: boolean) => {
    const action = pendingAction;
    setPendingAction(null);
    if (!action) return;
    if (save) {
      savePreconfigThenPerform(action);
    } else {
      performAction(action);
    }
  };

  /**
   * Applies a preconfig object to the UI state (checkboxes, radios, dates).
   * Does not call the print API; user can then adjust and click PRINT or FAX.
   */
  const applyPreconfigToState = (preconfig: any) => {
    if (!preconfig || typeof preconfig !== 'object') return;
    const {
      fromDate: configFrom,
      toDate: configTo,
      ...widgetConfig
    } = preconfig;

    // Reset all widgets to unchecked, then overlay preconfig selections
    const newState = {
      ...dashboardPrintConfig.reduce(
        (s, n) => ({ ...s, [n]: false }),
        {} as mumms.PrintWidgetConfigInterface,
      ),
    };
    const newRadioState = { ...radioState };

    Object.entries(widgetConfig).forEach(([name, value]) => {
      const componentName = name as mumms.ComponentName;
      if (!dashboardPrintConfig.includes(componentName)) return;
      if (value === true || value === false) {
        newState[componentName] = value;
        return;
      }
      const val = value as any;
      const view =
        componentName === ProgressNotes && val.ordersLayout
          ? [val.view || Orders, val.ordersLayout]
          : val.view;
      const { ordersLayout: _ordersLayout, ...selection } = val;
      newState[componentName] = {
        ...selection,
        ...(view !== undefined ? { view } : {}),
      };
      if (val?.view !== undefined) {
        newRadioState[componentName] = view;
      }
    });

    setState(newState);
    setRadioState(newRadioState);

    if (configFrom != null && configTo != null) {
      setPrintOptionsState(
        configFrom === configTo ? SingleDay : printOptionsConfig.secondOption,
      );
      const from = moment(configFrom).toDate();
      const to = moment(configTo).toDate();
      setFromDate(from);
      setToDate(to);
    } else {
      setPrintOptionsState(printOptionsConfig.defaultOption);
      setFromDate(undefined);
      setToDate(undefined);
    }
    setDateError(undefined);
  };

  const onSelectPredefined = (value: any) => {
    applyPreconfigToState(value);
  };

  const preconfigDisplayName =
    serverPreconfigs?.find((p) => p.id === lastAppliedPreconfigId)
      ?.displayName || lastAppliedPreconfigId;

  const resetPreconfigSaveIntent = () => {
    setPendingAction(null);
    setSaveFailedAction(null);
  };

  const applyPreconfigByName = (name: string) => {
    if (!name || !serverPreconfigs) return;
    const snap = serverPreconfigs.find((p) => p.id === name);
    if (!snap) return;
    const today = moment().format('YYYY-MM-DD');
    let withDates = snap.widgets;
    if (name === PRINT_PRECONFIG_IDS.idgComponents) {
      withDates = {
        ...snap.widgets,
        fromDate: moment().subtract(14, 'days').format('YYYY-MM-DD'),
        toDate: today,
      };
    } else if (name === PRINT_PRECONFIG_IDS.planOfCare) {
      withDates = { ...snap.widgets, fromDate: today, toDate: today };
    }
    onSelectPredefined(withDates);
    setPreconfigSelectValue(name);
    setLastAppliedPreconfigId(name as mumms.PrintPreconfigId);
    setLastAppliedWidgets(snap.widgets);
    resetPreconfigSaveIntent();
  };

  const handlePreconfigSelectChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    applyPreconfigByName(event.target.value as string);
  };

  const clearPreconfigSelection = () => {
    setPreconfigSelectValue('');
  };

  useEffect(() => {
    if (!dialogOpen) {
      resetPreconfigSaveIntent();
    }
  }, [dialogOpen]);

  // RADIO PRINT options values
  const [radioState, setRadioState] = React.useState(radioChoicePrintState);

  // eslint-disable-next-line consistent-return
  const handleRadioChange = (
    name: mumms.ComponentName,
    event: any,
    extended?: boolean,
  ) => {
    clearPreconfigSelection();
    // if "extended" radio view,  ex: [Orders, One per page]
    if (extended) {
      const radioName = event.target.value;
      const RadioStateExists = radioState && radioState[name];
      const OriginalRadioChoice = RadioStateExists && RadioStateExists[0];
      setRadioState({
        ...radioState,
        [name]: [OriginalRadioChoice, radioName],
      });
      const optionSelection = {
        view: [OriginalRadioChoice, radioName],
      };
      setState({
        ...state,
        [name]: optionSelection,
      });
      return;
    }
    const radioName = event.target.value;

    setRadioState({
      ...radioState,
      [name]: radioName,
    });
    const optionSelection = { view: radioName };

    let newConfig: any;
    if (!state[name]) {
      newConfig = optionSelection;
    } else {
      newConfig = state[name];
      Object.assign(newConfig, optionSelection);
    }

    setState({
      ...state,
      [name]: newConfig,
    });
  };

  useEffect(() => {
    const progressNotesRadioView = radioState[ProgressNotes];
    const progressNotesSelected = state[ProgressNotes];
    if (
      (progressNotesRadioView === Orders ||
        progressNotesRadioView === emptyText) &&
      progressNotesSelected
    ) {
      setRadioState({
        ...radioState,
        [ProgressNotes]: [Orders, OnePerPage],
      });
      setState({
        ...state,
        [ProgressNotes]: {
          view: [Orders, OnePerPage],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioState[ProgressNotes], state]);

  // radio buttons for the widget names
  const radioPrintGroup = (name: mumms.ComponentName) => {
    if (radioPrintConfig[name]) {
      const radioConfig: mumms.printConfigView[] = Object.values(
        radioPrintConfig[name]!,
      ) as mumms.printConfigView[];
      const radioPrintOptions = (_config: any) =>
        _config.map((option: any, i: number) => (
          <FormControlLabel
            id={`${name}-${option}-radio-button-${i + 1}`}
            value={option}
            key={option}
            control={
              <Radio size="small" classes={{ root: classes.denseToggle }} />
            }
            label={option}
            classes={{
              label: classes.radioLabel,
              root: `${classes.radioLabel} ${classes.radioOption}`,
            }}
          />
        ));

      const radioView = radioState && radioState[name];
      const extendedRadioOption =
        radioView && Array.isArray(radioView) && radioView[1];

      const ordersPrintConfig = [OnePerPage, MultiPerPage];
      const ordersRadioSelected =
        radioView &&
        (Array.isArray(radioView)
          ? radioView[0] === '' || radioView[0] === Orders
          : radioView === Orders);
      const ordersOptionsReadonly = !ordersRadioSelected;
      const ProgressNotesOrdersOptions = (disabled: boolean) =>
        ordersPrintConfig.map((option: any, i: number) => (
          <FormControlLabel
            id={`${name}-${option}-radio-button-${i + 1}`}
            value={option}
            key={option}
            disabled={disabled}
            control={
              <Radio size="small" classes={{ root: classes.denseToggle }} />
            }
            label={option}
            classes={{
              label: classes.radioLabel,
              root: `${classes.radioLabel} ${classes.radioOption}`,
            }}
          />
        ));

      const primaryRadioGroup = (
        <RadioGroup
          aria-label="radio-print"
          className={
            name === ProgressNotes
              ? `${classes.radioGroup} ${classes.progressNotesPrimaryRow}`
              : classes.radioGroup
          }
          name="radio-print"
          value={
            ordersRadioSelected
              ? Orders
              : radioState[name] || defaultRadioViewForWidget(name)
          }
          onChange={(event) => handleRadioChange(name, event)}
        >
          {radioPrintOptions(radioConfig)}
        </RadioGroup>
      );

      const secondaryRadioGroup =
        name === ProgressNotes && state[name] ? (
          <RadioGroup
            aria-label="radio-print-orders-layout"
            className={`${classes.radioGroup} ${classes.progressNotesSecondaryRow}`}
            name="radio-print-orders-layout"
            value={
              ordersOptionsReadonly
                ? MultiPerPage
                : extendedRadioOption || OnePerPage
            }
            onChange={
              ordersOptionsReadonly
                ? undefined
                : (event) => handleRadioChange(name, event, true)
            }
          >
            {ProgressNotesOrdersOptions(ordersOptionsReadonly)}
          </RadioGroup>
        ) : null;

      return name === ProgressNotes ? (
        <div className={classes.progressNotesRadioWrapper}>
          {primaryRadioGroup}
          {secondaryRadioGroup}
        </div>
      ) : (
        primaryRadioGroup
      );
    }
    return null;
  };

  const notesModeGroup = (name: mumms.ComponentName) => {
    if (!checkOptionPrintConfig[name]) {
      return <OptionsBox className={classes.optionColumn} />;
    }
    const current =
      typeof state[name] === 'object'
        ? (state[name] as mumms.WidgetPrintSelection).notesMode || 'none'
        : 'none';
    return (
      <OptionIconMenu
        value={current}
        icon={<NotesIcon mode={current} />}
        options={[
          { value: 'all', label: 'All notes' },
          { value: 'dateRange', label: 'Notes for date range' },
          { value: 'none', label: 'None' },
        ]}
        onChange={(value) => {
          clearPreconfigSelection();
          const notesMode = value as mumms.notesMode;
          const prev =
            typeof state[name] === 'object' ? (state[name] as any) : {};
          setState({ ...state, [name]: { ...prev, notesMode } });
        }}
      />
    );
  };

  const filterGroup = (name: mumms.ComponentName) => {
    if (!widgetHasIdgFilter(radioPrintConfigRaw[name])) {
      return <OptionsBox className={classes.optionColumn} />;
    }
    const current =
      typeof state[name] === 'object'
        ? (state[name] as mumms.WidgetPrintSelection).filter || ''
        : '';
    return (
      <OptionsBox className={classes.optionColumn}>
        <Checkbox
          checked={current === 'idgOnly'}
          onChange={(event) => {
            clearPreconfigSelection();
            const filter = event.target.checked ? 'idgOnly' : undefined;
            const prev =
              typeof state[name] === 'object' ? (state[name] as any) : {};
            const next = { ...prev };
            if (filter) next.filter = filter;
            else delete next.filter;
            setState({ ...state, [name]: next });
          }}
          size="small"
          color="primary"
          classes={{ root: classes.denseToggle }}
          inputProps={{ 'aria-label': 'IDG only' }}
        />
      </OptionsBox>
    );
  };

  const printOptionGroup = (name: mumms.ComponentName) => (
    <>
      <PrintOptionsDiv id={`print-options-dashboard-${name}`}>
        {radioPrintGroup(name)}
      </PrintOptionsDiv>
      <div className={classes.optionColumns}>
        {filterGroup(name)}
        {notesModeGroup(name)}
      </div>
    </>
  );

  const handlePrintOptionChange = (event: any) => {
    clearPreconfigSelection();
    setPrintOptionsState(event.target.value);
  };

  const getDatePickers = () => (
    <CalendarDiv>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <FormHelperText style={{ margin: 0 }}>
          {translations.fromDate as any}
        </FormHelperText>
        <ReactDatePicker
          portalId="root"
          popperPosition="fixed"
          popperPlacement="top"
          popperClassName={classes.datePickerPopper}
          selectedDate={fromDate}
          maximumDate={toDate}
          onChange={handleFromDateChange}
        />
      </div>
      {printOptionsState === SingleDay || (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <FormHelperText style={{ margin: 0 }}>
            {translations.toDate as any}
          </FormHelperText>
          <ReactDatePicker
            portalId="root"
            popperPosition="fixed"
            popperPlacement="top"
            popperClassName={classes.datePickerPopper}
            selectedDate={toDate}
            minimumDate={fromDate}
            onChange={handleToDateChange}
          />
        </div>
      )}
    </CalendarDiv>
  );

  const printOptionsGroup = () => {
    const dateOptions = Object.values(printOptionsConfig);

    const radioPrintOptions = () =>
      dateOptions.map((option: any, i) => (
        <FormControlLabel
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          value={option}
          control={
            <Radio size="small" classes={{ root: classes.denseToggle }} />
          }
          label={option}
          classes={{
            label: classes.radioLabel,
            root: classes.radioLabel,
          }}
        />
      ));

    return (
      <RadioGroup
        aria-label="radio-print"
        className={classes.printRadioGroup}
        name="radio-print"
        value={printOptionsState}
        onChange={handlePrintOptionChange}
      >
        {radioPrintOptions()}
      </RadioGroup>
    );
  };

  // eslint-disable-next-line consistent-return
  return (
    <>
      <PrintInterfaceDiv id="print-interface-div" className={classes.printRoot}>
        <div>
          <div className={classes.preconfigRow}>
            <div className={classes.preconfigRowInner}>
              <span className={classes.preconfigLabel}>
                {(defaults as any).preconfigs}:
              </span>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.preconfigSelect}
              >
                <Select
                  displayEmpty
                  disabled={!serverPreconfigs}
                  value={preconfigSelectValue}
                  onChange={handlePreconfigSelectChange}
                  labelId="preconfig-select-label"
                  id="preconfig-select"
                  classes={{ select: classes.preconfigSelectValue }}
                  MenuProps={{
                    PaperProps: {
                      className: classes.preconfigSelectMenuPaper,
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    {(defaults as any).preconfigPlaceholder}
                  </MenuItem>
                  {serverPreconfigs &&
                    serverPreconfigs.map((p) => (
                      <MenuItem
                        key={p.id}
                        value={p.id}
                        onClick={() => applyPreconfigByName(p.id)}
                      >
                        {p.displayName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <FormControl
            component="fieldset"
            margin="dense"
            className={classes.widgetFormControl}
          >
            <FormGroup>
              <div className={classes.optionHeaderRow}>
                <div className={classes.optionColumns}>
                  <span
                    className={`${classes.optionHeader} ${classes.optionColumn}`}
                  >
                    IDG
                  </span>
                  <span
                    className={`${classes.optionHeader} ${classes.optionColumn}`}
                  >
                    Notes
                  </span>
                </div>
              </div>
              {stateEntries.map((entry, i) => {
                const widgetName = entry[0];
                const label =
                  printConfigNames[widgetName as mumms.ComponentName];
                const widgetState = entry[1];
                return (
                  <PrintSelectionDiv key={widgetName}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!widgetState}
                          onChange={handleChange}
                          name={widgetName}
                          color="primary"
                          size="small"
                          classes={{
                            root: classes.checkBox,
                          }}
                        />
                      }
                      label={label}
                      className={`${classes.formControlLabel} ${classes.widgetLabel}`}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${widgetName}-${i}`}
                    />
                    {printOptionGroup(widgetName as any)}
                    {/* {radioOptionPrintGroup(widgetName as any)} */}
                  </PrintSelectionDiv>
                );
              })}
            </FormGroup>
          </FormControl>
        </div>
        {/* DATE RANGE PICKER */}
        {isPrintDateRange && (
          <div className={classes.dateRange}>
            <FormControl
              error={dateError}
              component="fieldset"
              className={classes.formControl}
            >
              {getDatePickers()}
              {dateError && (
                <FormHelperText> {translations.errorDatePrint}</FormHelperText>
              )}
            </FormControl>
          </div>
        )}
        {/* PRINT BUTTON SUBMIT */}
        <SubmitDiv id="submit-print-div">
          {printOptionsGroup()}
          <div className={classes.buttonRow}>
            <Button
              disabled={noWidgetsSelected || dateError}
              size="medium"
              variant="contained"
              color="primary"
              onClick={onEmail}
            >
              Email
            </Button>
            <Button
              disabled={noWidgetsSelected || dateError}
              size="medium"
              variant="contained"
              color="primary"
              onClick={onFax}
            >
              {defaults.fax}
            </Button>
            <Button
              disabled={noWidgetsSelected || dateError}
              size="medium"
              variant="contained"
              color="primary"
              onClick={onSubmit}
            >
              {translations.print}
            </Button>
          </div>
        </SubmitDiv>
      </PrintInterfaceDiv>
      <Dialog
        open={!!pendingAction}
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Save changes to the {preconfigDisplayName} preconfig?
        </DialogTitle>
        <DialogContent>
          You&apos;ve modified the <strong>{preconfigDisplayName}</strong>{' '}
          preconfig. Would you like to save your changes for all users?
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => answerSavePrompt(false)}>
            No
          </Button>
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={() => answerSavePrompt(true)}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!saveFailedAction}
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Could not save the {preconfigDisplayName} preconfig
        </DialogTitle>
        <DialogContent>
          Your print selections are unchanged. Nothing was sent.
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSaveFailedAction(null)}>
            Dismiss
          </Button>
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={() => {
              const action = saveFailedAction;
              setSaveFailedAction(null);
              if (action) performAction(action);
            }}
          >
            Continue without saving
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
